import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Modal from '../Modal';
import { DiseaseDto, CreateDiseaseDto, UpdateDiseaseDto, DiseaseCategoryDto, diseaseCategoriesApi } from '../../services/settingsService';
import { handleApiError } from '../../utils/errorHandler';

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
  
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  
  &.required::after {
    content: " *";
    color: var(--error-color);
  }
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }
  
  &::placeholder {
    color: var(--text-secondary);
  }
`;

const TextArea = styled.textarea<{ hasError?: boolean }>`
  padding: 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }
  
  &::placeholder {
    color: var(--text-secondary);
  }
`;

const Select = styled.select<{ hasError?: boolean }>`
  padding: 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  background-color: white;
  cursor: pointer;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  
  input[type="radio"] {
    margin: 0;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  
  input[type="checkbox"] {
    margin: 0;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 12px;
  margin-top: 4px;
`;

const CustomFieldsSection = styled.div`
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: 16px;
  margin-top: 16px;
`;

const CustomFieldsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const CustomFieldsTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const AddFieldButton = styled.button`
  padding: 8px 16px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    background: var(--primary-light); color: var(--primary-color);
  }
`;

const CustomFieldItem = styled.div`
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  padding: 12px;
  margin-bottom: 12px;
  background: var(--background-light);
`;

const CustomFieldGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 80px 40px;
  gap: 12px;
  align-items: center;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const CustomFieldInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DeleteFieldButton = styled.button`
  background: transparent;
  color: var(--error-color);
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #e91e63;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;
    
    &:hover {
      background: var(--primary-light); color: var(--primary-color);
    }
    
    &:disabled {
      background: var(--border-color);
      cursor: not-allowed;
    }
  ` : `
    background: var(--surface-color);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    
    &:hover {
      background: var(--hover-background);
    }
  `}
`;

interface CustomField {
  id?: number;
  fieldName: string;
  fieldType: number;
  isRequired: boolean;
  selectOptions?: string[];
  order: number;
}

interface DiseaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (disease: CreateDiseaseDto | UpdateDiseaseDto) => Promise<void>;
  disease?: DiseaseDto | null;
  title: string;
}

const DiseaseModal: React.FC<DiseaseModalProps> = ({
  isOpen,
  onClose,
  onSave,
  disease,
  title
}) => {
  const [formData, setFormData] = useState({
    name: '',
    categoryId: 0,
    type: 0, // Primary = 0, Secondary = 1
    description: '',
    isActive: true,
    customFields: [] as CustomField[]
  });
  
  const [categories, setCategories] = useState<DiseaseCategoryDto[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadCategories();
      if (disease) {
        setFormData({
          name: disease.name,
          categoryId: disease.categoryId,
          type: disease.type,
          description: disease.description || '',
          isActive: disease.isActive,
          customFields: disease.customFields?.map(field => ({
            id: field.id,
            fieldName: field.fieldName,
            fieldType: field.fieldType,
            isRequired: field.isRequired,
            selectOptions: field.selectOptions,
            order: field.order
          })) || []
        });
      } else {
        setFormData({
          name: '',
          categoryId: 0,
          type: 0,
          description: '',
          isActive: true,
          customFields: []
        });
      }
      setErrors({});
    }
  }, [isOpen, disease]);

  const loadCategories = async () => {
    try {
      const data = await diseaseCategoriesApi.getAll();
      setCategories(data);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בטעינת קטגוריות'));
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const addCustomField = () => {
    const newField: CustomField = {
      fieldName: '',
      fieldType: 0, // Text = 0
      isRequired: false,
      selectOptions: [],
      order: formData.customFields.length + 1
    };
    
    setFormData(prev => ({
      ...prev,
      customFields: [...prev.customFields, newField]
    }));
  };

  const updateCustomField = (index: number, field: keyof CustomField, value: any) => {
    setFormData(prev => ({
      ...prev,
      customFields: prev.customFields.map((cf, i) => 
        i === index ? { ...cf, [field]: value } : cf
      )
    }));
  };

  const removeCustomField = (index: number) => {
    setFormData(prev => ({
      ...prev,
      customFields: prev.customFields.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'שם המחלה הוא שדה חובה';
    }

    if (!formData.categoryId) {
      newErrors.categoryId = 'יש לבחור קטגוריה';
    }

    formData.customFields.forEach((field, index) => {
      if (!field.fieldName.trim()) {
        newErrors[`customField_${index}_name`] = 'שם השדה הוא חובה';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const diseaseData = {
        name: formData.name.trim(),
        categoryId: formData.categoryId,
        type: formData.type,
        description: formData.description.trim(),
        isActive: formData.isActive,
        customFields: formData.customFields.map((field, index) => ({
          id: field.id,
          fieldName: field.fieldName.trim(),
          fieldType: field.fieldType,
          isRequired: field.isRequired,
          selectOptions: field.fieldType === 3 ? field.selectOptions : undefined, // 3 = SelectList
          order: index + 1
        }))
      };

      await onSave(diseaseData);
      onClose();
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בשמירת מחלה'));
    } finally {
      setLoading(false);
    }
  };

  const fieldTypeOptions = [
    { value: 0, label: 'טקסט חופשי' },
    { value: 1, label: 'תאריך' },
    { value: 2, label: 'כן/לא' },
    { value: 3, label: 'רשימה' }
  ];

  const footerContent = (
    <>
      <Button variant="secondary" onClick={onClose}>
        ביטול
      </Button>
      <Button variant="primary" onClick={handleSave} disabled={loading}>
        {loading ? 'שומר...' : 'שמירה'}
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} footer={footerContent} width="600px">
        <FormSection>
          <FormGrid>
            <FormGroup>
              <Label className="required">שם המחלה</Label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="הזן שם מחלה"
                hasError={!!errors.name}
              />
              {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label className="required">קטגוריה</Label>
              <Select
                value={formData.categoryId}
                onChange={(e) => handleInputChange('categoryId', Number(e.target.value))}
                hasError={!!errors.categoryId}
              >
                <option value={0}>בחר קטגוריה</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
              {errors.categoryId && <ErrorMessage>{errors.categoryId}</ErrorMessage>}
            </FormGroup>
          </FormGrid>

          <FormGroup>
            <Label className="required">סוג המחלה</Label>
            <RadioGroup>
              <RadioOption>
                <input
                  type="radio"
                  name="diseaseType"
                  checked={formData.type === 0}
                  onChange={() => handleInputChange('type', 0)}
                />
                מחלה ראשונית
              </RadioOption>
              <RadioOption>
                <input
                  type="radio"
                  name="diseaseType"
                  checked={formData.type === 1}
                  onChange={() => handleInputChange('type', 1)}
                />
                מחלה משנית
              </RadioOption>
            </RadioGroup>
          </FormGroup>

          <FormGroup>
            <Label>תיאור</Label>
            <TextArea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="הזן תיאור (אופציונלי)"
            />
          </FormGroup>

          <CheckboxGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
              />
              פעיל
            </CheckboxLabel>
          </CheckboxGroup>
        </FormSection>

        <CustomFieldsSection>
          <CustomFieldsHeader>
            <CustomFieldsTitle>שדות מותאמים אישית</CustomFieldsTitle>
            <AddFieldButton onClick={addCustomField}>
              + הוסף שדה
            </AddFieldButton>
          </CustomFieldsHeader>

          {formData.customFields.map((field, index) => (
            <CustomFieldItem key={index}>
              <CustomFieldGrid>
                <CustomFieldInputWrapper>
                  <Input
                    type="text"
                    value={field.fieldName}
                    onChange={(e) => updateCustomField(index, 'fieldName', e.target.value)}
                    placeholder="שם השדה"
                    hasError={!!errors[`customField_${index}_name`]}
                  />
                  {errors[`customField_${index}_name`] && (
                    <ErrorMessage>{errors[`customField_${index}_name`]}</ErrorMessage>
                  )}
                </CustomFieldInputWrapper>

                <Select
                  value={field.fieldType}
                  onChange={(e) => updateCustomField(index, 'fieldType', Number(e.target.value))}
                >
                  {fieldTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>

                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={field.isRequired}
                    onChange={(e) => updateCustomField(index, 'isRequired', e.target.checked)}
                  />
                  חובה
                </CheckboxLabel>

                <DeleteFieldButton onClick={() => removeCustomField(index)}>
                  <Trash2 size={16} />
                </DeleteFieldButton>
              </CustomFieldGrid>

              {field.fieldType === 3 && (
                <FormGroup style={{ marginTop: '12px' }}>
                  <Label>אפשרויות רשימה (מופרדות בפסיק)</Label>
                  <Input
                    type="text"
                    value={field.selectOptions?.join(', ') || ''}
                    onChange={(e) => updateCustomField(index, 'selectOptions', e.target.value.split(', ').filter(Boolean))}
                    placeholder="אפשרות 1, אפשרות 2, אפשרות 3"
                  />
                </FormGroup>
              )}
            </CustomFieldItem>
          ))}

          {formData.customFields.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '20px' }}>
              לא הוגדרו שדות מותאמים אישית
            </div>
          )}
        </CustomFieldsSection>
    </Modal>
  );
};

export default DiseaseModal;